import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { StoreProvider } from "@/context/StoreContext";
import { Layout } from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Category from "@/pages/Category";
import Product from "@/pages/Product";
import Collection from "@/pages/Collection";
import GiftFinder from "@/pages/GiftFinder";
import Club from "@/pages/Club";
import Locations from "@/pages/Locations";
import Franchise from "@/pages/Franchise";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Cart from "@/pages/Cart";
import Favorites from "@/pages/Favorites";
import SearchPage from "@/pages/Search";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import Account from "@/pages/Account";
import PaymentResult from "@/pages/PaymentResult";
import NotFound from "@/pages/NotFound";

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function useLenis() {
  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
}

function AppShell() {
  useLenis();
  return (
    <>
      <ScrollManager />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/kategori/:slug" element={<Category />} />
          <Route path="/kategori/kolye" element={<Navigate to="/kategori/kolyeler" replace />} />
          <Route path="/kategori/kupe" element={<Navigate to="/kategori/kupeler" replace />} />
          <Route path="/kategori/yuzuk" element={<Navigate to="/kategori/yuzukler" replace />} />
          <Route path="/kategori/bileklik" element={<Navigate to="/kategori/bileklikler" replace />} />
          <Route path="/kategori/sac-aksesuar" element={<Navigate to="/kategori/sac-aksesuarlari" replace />} />
          <Route path="/urun/:slug" element={<Product />} />
          <Route path="/koleksiyon/:slug" element={<Collection />} />
          <Route path="/koleksiyon" element={<Navigate to="/kategori/tumu" replace />} />
          <Route path="/hediye" element={<GiftFinder />} />
          <Route path="/shule-club" element={<Club />} />
          <Route path="/magazalar" element={<Locations type="store" />} />
          <Route path="/kiosklar" element={<Locations type="kiosk" />} />
          <Route path="/bayilik" element={<Franchise />} />
          <Route path="/hakkimizda" element={<About />} />
          <Route path="/iletisim" element={<Contact />} />
          <Route path="/sepet" element={<Cart />} />
          <Route path="/favoriler" element={<Favorites />} />
          <Route path="/ara" element={<SearchPage />} />
          <Route path="/giris" element={<Auth />} />
          <Route path="/sifre-sifirla" element={<ResetPassword />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/hesap" element={<Account />} />
          <Route path="/odeme/basarili" element={<PaymentResult type="success" />} />
          <Route path="/odeme/iptal" element={<PaymentResult type="cancel" />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <StoreProvider>
          <BrowserRouter>
            <AppShell />
            <Toaster position="top-center" toastOptions={{
              style: { background: "#FDFBF7", border: "1px solid #D8C7B5", color: "#2C221E" },
            }} />
          </BrowserRouter>
        </StoreProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
